// src/features/fb/cookies/questions/CookiesQuestions.jsx
import React, { useState, useRef } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import BatchSizeQuestion from "./BatchSizeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {object} initialData - Initial data to populate the form
 */
export default function GlassbottlesQuestions({ onComplete, onDataChange, initialData }) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        partLength: "",
        partThickness: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 4; // Last question
    if (initialData.partWeightDimensions) return 2;
    if (initialData.batchSize) return 1;
    if (initialData.materialSelection) return 0;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  const [demoMode, setDemoMode] = useState(false);
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor = theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";
  
  // Check if all questions are answered
  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.batchSize &&
    parseFloat(productFormData.batchSize) > 0 &&
    productFormData.partWeightDimensions?.partLength &&
    productFormData.partWeightDimensions?.partThickness &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
    // Don't call onDataChange(null) when incomplete - this would wipe parent state
    // Parent should retain the last valid data for persistence across navigation
  }, [productFormData, isAllAnswered, onDataChange]);

  const productSelectedValues = [
    productFormData.productName && {
      label: "Part Name:",
      value: productFormData.productName,
    },
    productFormData.batchSize && {
      label: "Batch Size:",
      value: productFormData.batchSize,
    },
    productFormData.annualVolume && {
      label: "Annual Volume:",
      value: productFormData.annualVolume,
    },
    // Show Part Weight & Thickness after step 2 is answered
    productFormData.partWeightDimensions?.finishWeight &&
      productFormData.partWeightDimensions?.partThickness && {
        label: "Part Weight & Thickness:",
        value: `Finish weight: ${productFormData.partWeightDimensions.finishWeight} g, Thickness: ${productFormData.partWeightDimensions.partThickness} mm`,
      },
    productFormData.manufacturingCountry && {
      label: "Manufacturing Country:",
      value: productFormData.manufacturingCountry,
    },
  ].filter(Boolean); // Remove falsy values

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    const finishWeight = parseFloat(productFormData.partWeightDimensions?.finishWeight);
    const thickness = parseFloat(productFormData.partWeightDimensions?.partThickness);
    const isFinishWeightMissing = !productFormData.partWeightDimensions?.finishWeight;
    const isThicknessMissing = !productFormData.partWeightDimensions?.partThickness;
    const isFinishWeightOver = !isNaN(finishWeight) && finishWeight > 550;
    const isThicknessOver = !isNaN(thickness) && thickness > 10;
    const isFinishWeightNonPositive = !isNaN(finishWeight) && finishWeight <= 0;
    const isThicknessNonPositive = !isNaN(thickness) && thickness <= 0;

    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Batch Size
        return (
          !productFormData.batchSize ||
          productFormData.batchSize.trim() === "" ||
          parseFloat(productFormData.batchSize) <= 0
        );
      case 2: // Part Weight and Dimensions
        return (
          isFinishWeightMissing ||
          isThicknessMissing ||
          isFinishWeightOver ||
          isThicknessOver ||
          isFinishWeightNonPositive ||
          isThicknessNonPositive
        );
      case 3: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before moving to next step
  const handleStepChange = async (newStep) => {
    // Validation passed or not needed, proceed to next step
    setProductChatStep(newStep);
  };
  
  // Helper function to handle automatic navigation to next step on Enter key
  const handleNextStep = async (stepIndex) => {
    // Use handleStepChange to ensure consistent validation logic
    const nextStep = stepIndex + 1;
    const totalSteps = 4; // Total number of questions
    
    if (nextStep < totalSteps) {
      // Use handleStepChange which handles validation
      await handleStepChange(nextStep);
    } else {
      handleComplete();
    }
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        partLength: "",
        partThickness: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setProductChatStep(0);
    setDemoMode(false); // Reset demo toggle button
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "Perfume Glass Bottle",
        materialSelection: "",
        batchSize: "9000",
        partWeightDimensions: {
          partLength: "",
          partThickness: "2",
          finishWeight: "375",
        },
        marketPositioning: "",
        skuWeight: "",
        skuWeightUnit: "g",
        annualVolume: "108000",
        manufacturingCountry: "China",
      });
      // Navigate to the last step to show all filled values
      setProductChatStep(3);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
      />
      <BatchSizeQuestion
        value={productFormData.batchSize}
        onChange={(value) =>
          setProductFormData({
            ...productFormData,
            batchSize: value,
            annualVolume:
              value && !isNaN(parseFloat(value))
                ? Number(parseFloat(value) * 12)
                : "",
          })
        }
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partWeightDimensions: value })
        }
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
